import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';

import HeaderSection, { HeaderSectionType } from '../../components/layout/sections/HeaderSection';
import PageSection from '../../components/layout/sections/PageSection';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle, StrapiApiTag } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import BlogPostsWidget from '../../widgets/interactive/BlogPostsWidget';
import FeaturedBlogPostWidget from '../../widgets/interactive/FeaturedBlogPostWidget';

export const BlogFeaturedArticleId = 75;

const TabPanels = dynamic(() => import('../../components/interactive/TabPanels'), { ssr: false });

type BlogPageStaticProps = {
  featuredArticle: StrapiApiArticle;
  articlesByTag: Record<string, { tag: StrapiApiTag; articles: StrapiApiArticle[] }>;
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<BlogPageStaticProps> = async (context) => {
  // Fetch all articles and tags
  const tags = await StrapiApiClient.getAllTags();
  const articles = await StrapiApiClient.getAllArticles();
  // Create a map of tags to articles to use for the blog page
  const articlesByTag = tags.reduce((acc, tag) => {
    const articlesForTag = articles.filter((article) => article.attributes.tags?.data.some((articleTag) => articleTag.id === tag.id));
    return { ...acc, [tag.attributes.name]: { tag, articles: articlesForTag } };
  }, {});
  // Choose the featured article
  const featuredArticle = articles.find((article) => article.id === BlogFeaturedArticleId) || articles[0];
  // Return the props along with the default props
  const props: BlogPageStaticProps = { articles, articlesByTag, featuredArticle };
  return withGlobalDefaultStaticProps(context, { props });
};

const BlogPage: NextPageWithLayout<BlogPageStaticProps> = (props) => {
  const { articles, articlesByTag, featuredArticle } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Blog);
  // The default panel is the list of all articles
  const defaultTabPanel = useMemo(
    () => ({ tabId: 'all', tabLabel: 'All Posts', content: <BlogPostsWidget articles={articles} /> }),
    [articles],
  );
  // Create the panels for the tab list widget
  const tagTabPanels = useMemo(() => {
    return Object.values(articlesByTag).map(({ tag, articles }) => ({
      tabId: String(tag.attributes.name).toLowerCase().replace(/\s/g, '-'),
      tabLabel: tag.attributes.name,
      content: <BlogPostsWidget articles={articles} />,
    }));
  }, [articlesByTag]);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection type={HeaderSectionType.Black} title="Blog Posts">
        <FeaturedBlogPostWidget article={featuredArticle} />
      </HeaderSection>
      <PageSection isNarrow={true}>
        <h1>Latest</h1>
        <TabPanels panelColumnMinWidth={300} panels={[defaultTabPanel, ...tagTabPanels]} />
      </PageSection>
    </>
  );
};
BlogPage.displayName = 'BlogPage';
export default BlogPage;
